// src/components/UploadedFilesList.tsx

import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Paper,
  Box,
  IconButton,
  Chip
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';

interface UploadedFilesListProps {
  title: string;
  files: File[];
  /**
   * Optional callback fired when a file should be removed.
   */
  onRemoveFile?: (file: File) => void;
  /**
   * Optional map of filename to password (for displaying lock icon on protected files)
   */
  filePasswords?: Record<string, string>;
}

const UploadedFilesList: React.FC<UploadedFilesListProps> = ({ title, files, onRemoveFile, filePasswords }) => {
  if (files.length === 0) return null;
  
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {files.length} {title} selected:
      </Typography>
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <List disablePadding>
          {files.map((file, index) => {
            const hasPassword = filePasswords && filePasswords[file.name];
            return (
              <ListItem key={index} sx={{ borderBottom: "1px solid var(--border)" }}>
                <ListItemIcon>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <InsertDriveFileIcon sx={{ color: 'hsl(var(--accent))' }} />
                    {hasPassword && (
                      <LockIcon 
                        sx={{ 
                          position: 'absolute',
                          bottom: -4,
                          right: -4,
                          fontSize: 14,
                          color: 'success.main',
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                        }} 
                      />
                    )}
                  </Box>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {file.name}
                      {hasPassword && (
                        <Chip 
                          label="Unlocked" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={`${(file.size / 1024).toFixed(2)} KB`} 
                />
                {onRemoveFile && (
                  <IconButton 
                    edge="end" 
                    onClick={() => onRemoveFile(file)}
                    sx={{
                      '&:hover svg': {
                        color: '#4a90e2'
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </ListItem>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

export default UploadedFilesList;
